import { Button, Menu, MenuItem, MenuList, MenuPopover, MenuTrigger, Tooltip } from "@fluentui/react-components";
import BookmarkOutlineIcon from "~icons/material-symbols/bookmark-outline";
import ContentCopyOutlineIcon from "~icons/material-symbols/content-copy-outline";
import DescriptionOutlineIcon from "~icons/material-symbols/description-outline";
import InsertTextOutlineIcon from "~icons/material-symbols/insert-text-outline";
import PendingOutlineIcon from "~icons/material-symbols/pending-outline";
import StarOutlineIcon from "~icons/material-symbols/star-outline";
import { useCallback } from "react";
import { useToastNotification } from "../../../hooks";
import { IClause } from "../../../interfaces";
import { bookmarksApi, favoritesApi } from "../../../services";
import { ClauseCardProps } from "../../../types/ClauseCardProps";
import { RelatedObjectType } from "../../../types/enums";
import { ToastMessage } from "./ToastMessage";

export const SideActionRenderMenu = (props: ClauseCardProps) => {
  const { notify, dismissNotification } = useToastNotification();
  const [asyncBookmarkClause] = bookmarksApi.useSaveBookmarkMutation();
  const [asyncFavoriteClause] = favoritesApi.useSetClauseFavoriteMutation();

  const onClickCopyToClipboard = useCallback(
    (clauseText: string) => {
      if (navigator && navigator.clipboard) {
        navigator.clipboard.writeText(clauseText);
        notify(
          (id: string) => (
            <ToastMessage
              toastId={id}
              message="Clause copied to clipboard successfully."
              onDismiss={dismissNotification}
            />
          ),
          "success"
        );
      } else {
        notify(
          (id: string) => (
            <ToastMessage
              toastId={id}
              message="Unsupported browser for clipboard copy."
              onDismiss={dismissNotification}
            />
          ),
          "error"
        );
      }
    },
    [notify, dismissNotification]
  );

  const onClickAddToBookmark = useCallback(
    async (props: ClauseCardProps) => {
      try {
        const sourceClauses = props.clause.sourceDocuments.map((document) => ({
          ...props.clause,
          clause_id: props.clause.clause_id,
          clause_text: document.doc_date,
          title: document.doc_title,
          textBlockId: document.id,
        }));

        await Promise.all(
          sourceClauses.map(async (clause) => {
            await asyncBookmarkClause({
              bookmark: {
                relatedId: clause.clause_id,
                relatedObjectType: RelatedObjectType.CLAUSE,
                bookmark: clause.is_bookmarked ? null : "bookmark",
              },
              workspaceId: props.workspaceId,
            }).unwrap();
          })
        );

        const totalBookmarked = sourceClauses.length;
        const actionMessage = props.clause.is_bookmarked ? "removed from bookmarks" : "bookmarked";
        notify(
          (id: string) => (
            <ToastMessage
              message={
                totalBookmarked > 1
                  ? `${totalBookmarked} clauses ${actionMessage} successfully.`
                  : `Clause ${actionMessage} successfully.`
              }
              toastId={id}
              onDismiss={dismissNotification}
            />
          ),
          "success"
        );
      } catch (err: any) {
        notify(
          (id: string) => (
            <ToastMessage
              message={`Error bookmarking clauses. ${err.message}`}
              toastId={id}
              onDismiss={dismissNotification}
            />
          ),
          "error"
        );
      }
    },
    [asyncBookmarkClause, notify, dismissNotification]
  );

  const onClickAddToFavorite = useCallback(
    async (clause: IClause, workspaceId: string) => {
      try {
        await asyncFavoriteClause({
          id: clause.clause_id,
          isFavorite: !clause.is_favorite,
          workspaceId,
        }).unwrap();

        notify(
          (id: string) => (
            <ToastMessage
              toastId={id}
              message={
                clause.is_favorite
                  ? "Clause removed from favorites successfully."
                  : "Clause added to favorites successfully."
              }
              onDismiss={dismissNotification}
            />
          ),
          "success"
        );
      } catch (err: any) {
        notify(
          (id: string) => (
            <ToastMessage
              toastId={id}
              message={`Error adding to favorite. ${err.message}`}
              onDismiss={dismissNotification}
            />
          ),
          "error"
        );
      }
    },
    [asyncFavoriteClause, notify, dismissNotification]
  );
  return (
    <Menu positioning={"after-bottom"}>
      <MenuTrigger disableButtonEnhancement>
        <Tooltip content="Options" relationship="label" positioning="after">
          <Button size="small" appearance="transparent" icon={<PendingOutlineIcon color="#242424" />} />
        </Tooltip>
      </MenuTrigger>
      <MenuPopover>
        <MenuList>
          <MenuItem icon={<InsertTextOutlineIcon />} disabled>
            Smart insert
          </MenuItem>
          <MenuItem
            icon={<DescriptionOutlineIcon />}
            onClick={() => props.onOpenDocument?.(props.clause.sourceDocuments?.[0]!, props.clause)}
          >
            View Document
          </MenuItem>
          <MenuItem icon={<ContentCopyOutlineIcon />} onClick={() => onClickCopyToClipboard(props.clause.clause_text)}>
            Copy text to clipboard
          </MenuItem>
          <MenuItem
            icon={<BookmarkOutlineIcon color={props.clause.is_bookmarked ? "#EAB308" : undefined} />}
            onClick={() => onClickAddToBookmark(props)}
          >
            {props.clause.is_bookmarked ? "Added to bookmarks" : "Add to bookmarks"}
          </MenuItem>
          <MenuItem
            icon={<StarOutlineIcon color={props.clause.is_favorite ? "#EAB308" : undefined} />}
            onClick={() => onClickAddToFavorite(props.clause, props.workspaceId)}
          >
            {props.clause.is_favorite ? "Added to favorites" : "Add to favorites"}
          </MenuItem>
        </MenuList>
      </MenuPopover>
    </Menu>
  );
};
